import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { EnrichedCompletedObjective, PlayerDetail, OperationObjectiveInfo } from '../../types/gameDetail';

const ObjectivesContainer = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  padding: 20px;
`;


const ObjectivesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ObjectiveItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  border-left: 4px solid #4CAF50;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.15);
  }
`;

const OrderNumber = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(76, 175, 80, 0.2);
  border: 2px solid #4CAF50;
  border-radius: 50%;
  font-size: 16px;
  font-weight: 700;
  color: #4CAF50;
  font-family: 'Orbitron', sans-serif;
`;

const ObjectiveContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ObjectiveTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const ObjectiveDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: #8bb8d8;
`;

const DetailItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DeviceName = styled.span`
  color: #FF9800;
  font-weight: 500;
`;

const PlayerName = styled.span<{ $isClickable: boolean }>`
  color: #00d4ff;
  font-weight: 500;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;

  ${props => props.$isClickable && `
    &:hover {
      color: #fff;
      text-shadow: 0 0 8px #00d4ff;
    }
  `}
`;

const CompletedTime = styled.div`
  text-align: right;
  min-width: 100px;
`;

const TimeValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #4CAF50;
  font-family: 'Orbitron', sans-serif;
`;

const DateValue = styled.div`
  font-size: 10px;
  color: #8bb8d8;
  margin-top: 2px;
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4CAF50;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #8bb8d8;
  font-size: 14px;
`;

const FactionBadge = styled.span<{ $faction: string }>`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    const faction = props.$faction?.toLowerCase();
    if (faction === 'human' || faction === 'humans') return '#3b82f6';
    if (faction === 'alien' || faction === 'aliens') return '#ff8800';
    return '#9E9E9E';
  }};
  color: ${props => {
    const faction = props.$faction?.toLowerCase();
    if (faction === 'human' || faction === 'humans') return '#000';
    return '#fff';
  }};
  text-shadow: none;
`;

const getFactionDisplayName = (faction: string): string => {
  const f = faction?.toLowerCase();
  if (f === 'human' || f === 'humans') return 'Human';
  if (f === 'alien' || f === 'aliens') return 'Alien';
  return faction;
};

interface CompletedObjectivesListProps {
  objectives: EnrichedCompletedObjective[];
  players?: PlayerDetail[];
  allObjectives?: OperationObjectiveInfo[];
}

const CompletedObjectivesList: React.FC<CompletedObjectivesListProps> = ({ objectives, players = [], allObjectives = [] }) => {
  const navigate = useNavigate();

  // Создаем map для быстрого поиска фракции по playerId
  const playerFactionMap = React.useMemo(() => {
    const map = new Map<string, string>();
    players.forEach(player => {
      map.set(player.userId, player.faction);
    });
    return map;
  }, [players]);

  // Создаем map для быстрого поиска информации о цели по objectiveId
  const objectiveInfoMap = React.useMemo(() => {
    const map = new Map<string, OperationObjectiveInfo>();
    allObjectives.forEach(obj => {
      map.set(obj.objectiveId, obj);
    });
    return map;
  }, [allObjectives]);

  // Форматируем время без конвертации UTC - показываем локальное время поля
  const formatTime = (timestamp: string) => {
    const match = timestamp.match(/T(\d{2}):(\d{2}):(\d{2})/);
    if (!match) return timestamp;
    return `${match[1]}:${match[2]}:${match[3]}`;
  };

  // Форматируем дату без конвертации UTC - показываем локальное время поля
  const formatDate = (timestamp: string) => {
    const match = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return timestamp;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(match[2], 10) - 1];
    return `${monthName} ${parseInt(match[3], 10)}`;
  };

  const handlePlayerClick = (playerId?: string) => {
    if (playerId) {
      navigate(`/profile/${playerId}`);
    }
  };

  // Sort by completedAt (oldest first - chronological order)
  const sortedObjectives = [...objectives].sort((a, b) =>
    new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime()
  );

  if (objectives.length === 0) {
    return (
      <ObjectivesContainer>
        <EmptyState>No objectives were completed in this mission</EmptyState>
      </ObjectivesContainer>
    );
  }

  return (
    <ObjectivesContainer>
      <ObjectivesList>
        {sortedObjectives.map((objective, index) => (
          <ObjectiveItem key={`${objective.objectiveId}-${objective.fmdId}-${index}`}>
            <OrderNumber>{index + 1}</OrderNumber>

            <ObjectiveContent>
              <ObjectiveTitle>
                {objective.objectiveDescription || `Objective #${index + 1}`}
              </ObjectiveTitle>

              <ObjectiveDetails>
                <DetailItem>
                  <span>Device:</span>
                  <DeviceName>{objective.deviceName}</DeviceName>
                </DetailItem>

                {objectiveInfoMap.get(objective.objectiveId)?.targetCount && (
                  <DetailItem>
                    <span>Target:</span>
                    <span style={{ color: '#4CAF50', fontWeight: 600 }}>
                      {objectiveInfoMap.get(objective.objectiveId)?.targetCount}
                    </span>
                  </DetailItem>
                )}

                {objective.playerName && (
                  <>
                    <DetailItem>
                      <span>Completed by:</span>
                      <PlayerName
                        $isClickable={!!objective.playerId}
                        onClick={() => handlePlayerClick(objective.playerId)}
                      >
                        {objective.playerName}
                      </PlayerName>
                    </DetailItem>
                    {objective.playerId && playerFactionMap.get(objective.playerId) && (
                      <FactionBadge $faction={playerFactionMap.get(objective.playerId) || ''}>
                        {getFactionDisplayName(playerFactionMap.get(objective.playerId) || '')}
                      </FactionBadge>
                    )}
                  </>
                )}
              </ObjectiveDetails>
            </ObjectiveContent>

            <CompletedTime>
              <TimeValue>{formatTime(objective.completedAt)}</TimeValue>
              <DateValue>{formatDate(objective.completedAt)}</DateValue>
            </CompletedTime>

            <CheckIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M20 6L9 17L4 12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </CheckIcon>
          </ObjectiveItem>
        ))}
      </ObjectivesList>
    </ObjectivesContainer>
  );
};

export default CompletedObjectivesList;
